const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const node = (tag, text, attributes = {}) => {
  const element = document.createElement(tag);
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  return element;
};
const stale = () => new Error('선택한 페이지나 항목이 바뀌었습니다. 현재 항목을 확인한 뒤 다시 적용해 주세요.');
const imageName = item => {
  if (typeof item.image?.name === 'string' && item.image.name.trim()) return item.image.name.trim();
  return item.name.trim() || '이미지';
};
const textSnippet = item => {
  const text = item.text.replace(/\s+/g, ' ').trim();
  if (!text) return item.name.trim() || '빈 글';
  const characters = Array.from(text);
  return characters.length > 80 ? `${characters.slice(0, 80).join('')}…` : text;
};

// Content edits stay in the original native document. This panel does not
// reimplement text styling, layer layout, image placement, or native history.
export function mountNativeContentPanel({ getNative, getSectionId, run, onEdited, onDesign, getImageLimit = () => 1024 * 1024 }) {
  let disposed = false, pending = false, current = null, selectedKey = null;
  let refreshing = null, refreshAgain = false, listSignature = '', listNative = null;
  const drafts = new Map();
  const element = node('section', '', { class: 'es-native-content', 'aria-label': '선택한 콘텐츠 편집' });
  const listElement = node('section', '', { class: 'es-native-content-list', 'aria-label': '페이지의 글과 이미지' });
  const list = node('div', '', { class: 'es-native-content-items' });
  listElement.append(node('h3', '글과 이미지'), list);
  const status = node('p', '글이나 이미지를 선택해 주세요.', { class: 'es-native-content-status', role: 'status' });
  const error = node('p', '', { class: 'es-native-content-error', role: 'alert', hidden: '' });
  const fields = node('div', '', { class: 'es-native-content-fields', hidden: '' });
  const title = node('h3');
  const textField = node('label', '', { class: 'es-native-content-field' });
  const textarea = node('textarea', '', { 'aria-label': '선택한 글 내용', rows: '8' });
  textField.append(node('span', '선택한 글 내용'), textarea);
  const apply = node('button', '내용 적용', { type: 'button', class: 'es-native-content-apply' });
  const imageInfo = node('p', '', { class: 'es-native-content-image-info' });
  const imageField = node('label', '', { class: 'es-native-content-field' });
  const imageInput = node('input', '', { type: 'file', accept: [...IMAGE_TYPES].join(','), 'aria-label': '선택한 이미지 교체' });
  const imageLimit = node('span');
  imageField.append(imageLimit, imageInput);
  const note = node('p', '', { class: 'es-native-content-note' });
  const design = node('button', '디자인에서 편집', { type: 'button', class: 'es-native-content-design' });
  design.addEventListener('click', () => { if (!disposed) onDesign(); });
  fields.append(title, textField, apply, imageInfo, imageField, note, design);
  element.append(status, error, fields);

  const sameScope = context => !disposed && context.native === getNative() && context.sectionId === getSectionId();
  const keyFor = (context, id) => JSON.stringify([context.sectionId, context.data.fileId, context.data.pageId, id]);
  const selection = context => context?.data.selection.length === 1
    ? context.data.items.find(item => item.id === context.data.selection[0]) : null;
  const report = reason => { error.textContent = reason instanceof Error ? reason.message : String(reason); error.hidden = false; };
  function validate(data) {
    if (!data || typeof data.fileId !== 'string' || !data.fileId || typeof data.pageId !== 'string' || !data.pageId ||
      !Array.isArray(data.items) || !Array.isArray(data.selection) || data.selection.some(id => typeof id !== 'string') ||
      data.items.some(item => !item || typeof item.id !== 'string' || !item.id || typeof item.name !== 'string' ||
        !['text', 'image'].includes(item.type) || typeof item.editable !== 'boolean' || (item.type === 'text' && typeof item.text !== 'string')) ||
      new Set(data.items.map(item => item.id)).size !== data.items.length) {
      throw new Error('원본 편집기의 콘텐츠 응답을 확인할 수 없습니다. 디자인 편집에서 확인해 주세요.');
    }
    return data;
  }
  function draftFor(context, item) {
    const key = keyFor(context, item.id);
    let draft = drafts.get(key);
    if (!draft || draft.value === draft.expectedText) {
      draft = { value: item.text, expectedText: item.text };
      drafts.set(key, draft);
    }
    return draft;
  }
  function render() {
    if (!current || !sameScope(current)) { fields.hidden = true; selectedKey = null; return; }
    const item = selection(current);
    const signature = JSON.stringify([current.sectionId, current.data.fileId, current.data.pageId,
      current.data.items.map(({ id, name, type, depth, text, image }) => ({ id, name, type, depth, text, imageName: image?.name, imageUrl: image?.url })),
      current.data.selection]);
    if (signature !== listSignature || current.native !== listNative) {
      listSignature = signature; listNative = current.native;
      const imageCounts = new Map(), imageOrdinals = new Map();
      for (const entry of current.data.items) {
        if (entry.type === 'image') {
          const name = imageName(entry);
          imageCounts.set(name, (imageCounts.get(name) ?? 0) + 1);
        }
      }
      list.replaceChildren(...current.data.items.map(entry => {
        const button = node('button', '', {
          type: 'button', class: 'es-native-content-item', 'data-content-id': entry.id,
          'aria-pressed': String(current.data.selection.includes(entry.id)), title: entry.name,
        });
        let label = `글 · ${entry.type === 'text' ? textSnippet(entry) : ''}`;
        if (entry.type === 'image') {
          const name = imageName(entry), ordinal = (imageOrdinals.get(name) ?? 0) + 1;
          imageOrdinals.set(name, ordinal);
          label = name === '이미지' ? name : `이미지 · ${name}`;
          if (imageCounts.get(name) > 1) label += ` (${ordinal})`;
          if (typeof entry.image?.url === 'string' && entry.image.url) {
            const thumbnail = node('img', '', { class: 'es-native-content-thumbnail', src: entry.image.url,
              alt: '', width: '32', height: '32', loading: 'lazy', decoding: 'async' });
            thumbnail.addEventListener('error', () => { thumbnail.hidden = true; });
            button.append(thumbnail);
          }
        }
        button.append(node('span', label, { class: 'es-native-content-item-label' }));
        const context = current;
        button.addEventListener('click', () => { void perform(context, async () => {
          await inspect(context, entry.id, false);
          await context.native.execute('select_content', { id: entry.id, fileId: context.data.fileId, pageId: context.data.pageId });
          if (sameScope(context)) await refresh();
        }); });
        return button;
      }));
    }
    fields.hidden = !item;
    if (!item) {
      selectedKey = null;
      status.textContent = current.data.selection.length > 1 ? '글이나 이미지를 하나만 선택해 주세요.'
        : current.data.items.length ? '왼쪽 목록이나 화면에서 글과 이미지를 선택해 주세요.' : '이 페이지에는 편집할 글이나 이미지가 없습니다.';
      return;
    }
    const nextKey = keyFor(current, item.id);
    if (nextKey !== selectedKey) error.hidden = true;
    selectedKey = nextKey;
    title.textContent = item.name;
    status.textContent = item.type === 'text' ? '원본의 서식을 유지하며 글 내용을 바꿉니다.' : '원본의 위치와 크기를 유지하며 이미지를 바꿉니다.';
    textField.hidden = apply.hidden = item.type !== 'text';
    imageInfo.hidden = imageField.hidden = item.type !== 'image';
    design.hidden = item.editable;
    note.textContent = item.editable ? '' : `${item.reason || '이 항목은 콘텐츠 화면에서 수정할 수 없습니다.'} 원본 디자인 편집에서 수정해 주세요.`;
    if (item.type === 'text') {
      const draft = draftFor(current, item);
      if (textarea.value !== draft.value) textarea.value = draft.value;
      textarea.readOnly = !item.editable;
      textarea.disabled = pending;
      apply.disabled = pending || !item.editable || draft.value === draft.expectedText;
      if (item.editable && draft.value !== draft.expectedText && item.text !== draft.expectedText) {
        note.textContent = '원본 글이 다른 곳에서 변경되었습니다. 입력한 내용은 보관했습니다. 디자인에서 원본을 확인해 주세요.';
        design.hidden = false;
      }
    } else {
      imageLimit.textContent = `이미지 교체 · ${getImageLimit() / 1024 / 1024}MiB 이하`;
      imageInput.disabled = pending || !item.editable;
      const image = item.image;
      imageInfo.textContent = image ? [image.name, image.mime, image.width && image.height ? `${image.width} × ${image.height}` : ''].filter(Boolean).join(' · ') : item.name;
    }
  }
  async function refresh() {
    if (disposed) return;
    if (refreshing) { refreshAgain = true; return refreshing; }
    refreshing = (async () => {
      do {
        refreshAgain = false;
        const context = { native: getNative(), sectionId: getSectionId() };
        if (!context.native || !context.native.getStatus().connected) {
          current = null; listSignature = ''; list.replaceChildren(); fields.hidden = true; selectedKey = null;
          status.textContent = '원본 편집기가 연결되면 글과 이미지를 편집할 수 있습니다.';
          continue;
        }
        try {
          const data = validate(await context.native.execute('content_items'));
          if (!sameScope(context)) continue;
          current = { ...context, data };
          render();
        } catch (reason) {
          if (!sameScope(context)) continue;
          current = null; listSignature = ''; list.replaceChildren(); fields.hidden = true; selectedKey = null;
          status.textContent = '콘텐츠 편집을 준비하지 못했습니다. 원본 디자인 편집에서 확인해 주세요.';
          report(reason);
        }
      } while (refreshAgain && !disposed);
    })();
    try { await refreshing; } finally { refreshing = null; }
  }
  async function inspect(context, id, mustSelect = true) {
    if (!sameScope(context)) throw stale();
    const data = validate(await context.native.execute('content_items'));
    if (!sameScope(context) || data.fileId !== context.data.fileId || data.pageId !== context.data.pageId ||
      (mustSelect && (data.selection.length !== 1 || data.selection[0] !== id))) throw stale();
    const item = data.items.find(item => item.id === id);
    if (!item) throw stale();
    return item;
  }
  async function perform(context, action) {
    if (disposed || pending) return;
    pending = true; error.hidden = true; render();
    try { await run(async () => { if (!sameScope(context)) throw stale(); await action(); }); }
    catch (reason) { if (sameScope(context)) report(reason); }
    finally { pending = false; if (!disposed) render(); }
  }
  textarea.addEventListener('input', () => {
    if (!current || !sameScope(current) || !selectedKey) return;
    const draft = drafts.get(selectedKey);
    if (draft) { draft.value = textarea.value; render(); }
  });
  apply.addEventListener('click', () => {
    const context = current, item = selection(context);
    if (!item || item.type !== 'text' || !item.editable) return;
    const draft = drafts.get(keyFor(context, item.id));
    const text = draft.value, expectedText = draft.expectedText;
    void perform(context, async () => {
      const latest = await inspect(context, item.id);
      if (latest.type !== 'text' || !latest.editable) throw new Error('이 글은 원본 디자인 편집에서 수정해 주세요.');
      await context.native.execute('update_content', { id: item.id, fileId: context.data.fileId, pageId: context.data.pageId, text, expectedText });
      draft.expectedText = text;
      if (sameScope(context)) { onEdited(); await refresh(); }
    });
  });
  imageInput.addEventListener('change', () => {
    const context = current, item = selection(context), file = imageInput.files[0];
    imageInput.value = '';
    if (!file || !item || item.type !== 'image' || !item.editable) return;
    void perform(context, async () => {
      if (!IMAGE_TYPES.has(file.type)) throw new Error('PNG, JPEG, WebP, GIF 이미지를 선택해 주세요.');
      const limit = getImageLimit();
      if (!file.size || file.size > limit) throw new Error(`${limit / 1024 / 1024}MiB 이하의 이미지를 선택해 주세요.`);
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
      const latest = await inspect(context, item.id);
      if (latest.type !== 'image' || !latest.editable) throw new Error('이 이미지는 원본 디자인 편집에서 수정해 주세요.');
      await context.native.execute('update_content', { id: item.id, fileId: context.data.fileId, pageId: context.data.pageId,
        image: { name: file.name, mime: file.type, data: btoa(binary) } });
      if (sameScope(context)) { onEdited(); await refresh(); }
    });
  });
  return { element, listElement, refresh, dispose() {
    disposed = true; current = null; drafts.clear(); element.remove(); listElement.remove();
  } };
}
